/**
 * Member Notifications Page
 * 
 * Shows notifications including outstanding invoices and payment verification notices
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import apiClient from '@/utils/api';
import { formatCurrency } from '@/utils/format';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import { trackEvent, USAGE_EVENTS } from '@/utils/usageTracker';
import { 
  Bell, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  CreditCard,
  FileText,
  Clock,
  Inbox,
  Check
} from 'lucide-react';

const MemberNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent(USAGE_EVENTS.VIEW_NOTIFICATIONS);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [notifsRes, invoicesRes, balanceRes] = await Promise.all([
        apiClient.get('/notifications/my'),
        apiClient.get('/members/invoices'),
        apiClient.get('/members/balance')
      ]);
      setNotifications(notifsRes.data || []);
      setInvoices(invoicesRes.data || []);
      setBalance(balanceRes.data);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const getNotificationIcon = (notif) => {
    const title = notif.title?.toLowerCase() || '';
    const message = notif.message?.toLowerCase() || '';
    
    if (title.includes('verified') || message.includes('verified')) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    if (title.includes('rejected') || message.includes('rejected')) {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
    if (title.includes('invoice') || message.includes('invoice')) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    if (title.includes('payment') || message.includes('payment')) {
      return <CreditCard className="w-5 h-5 text-purple-600" />;
    }
    return <Bell className="w-5 h-5 text-primary" />;
  };

  const getNotificationStyle = (notif) => {
    const title = notif.title?.toLowerCase() || '';
    const message = notif.message?.toLowerCase() || '';
    
    if (title.includes('verified') || message.includes('verified')) {
      return 'border-green-200 bg-green-50';
    }
    if (title.includes('rejected') || message.includes('rejected')) {
      return 'border-red-200 bg-red-50';
    }
    return '';
  };

  const currency = balance?.currency || 'USD';
  const unpaidInvoices = invoices.filter(inv => inv.status === 'unpaid');
  const unreadNotifications = notifications.filter(n => !n.is_read);
  const outstandingBalance = balance?.outstanding_balance || 0;

  if (loading) {
    return (
      <MemberLayout currentPage="notifications" title="Notifications" notificationCount={0}>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="notifications" title="Notifications" notificationCount={unreadNotifications.length}>
      {/* Outstanding Invoices Alert */}
      {unpaidInvoices.length > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              Outstanding Invoices
            </CardTitle>
            <CardDescription className="text-amber-600">
              You have {unpaidInvoices.length} unpaid invoice(s) totaling {formatCurrency(outstandingBalance, currency)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {unpaidInvoices.slice(0, 3).map((invoice) => (
                <div 
                  key={invoice.invoice_id} 
                  className="flex justify-between items-center p-3 bg-white rounded-lg border border-amber-200"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <div>
                      <p className="font-medium text-sm">{invoice.invoice_number}</p>
                      <p className="text-xs text-muted-foreground">{invoice.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-amber-700">{formatCurrency(invoice.amount, currency)}</p>
                    <p className="text-xs text-muted-foreground">
                      Due: {new Date(invoice.due_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
              {unpaidInvoices.length > 3 && (
                <p className="text-sm text-amber-600 text-center">
                  +{unpaidInvoices.length - 3} more unpaid invoice(s)
                </p>
              )}
            </div>
            <Link to="/dashboard/payments">
              <Button className="w-full mt-4 bg-amber-600 hover:bg-amber-700" data-testid="pay-now-btn">
                <CreditCard className="w-4 h-4 mr-2" />
                View & Pay Now
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Notifications Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold">All Notifications</h2>
          <p className="text-muted-foreground">
            {unreadNotifications.length > 0 
              ? `${unreadNotifications.length} unread notification(s)`
              : 'All caught up!'}
          </p>
        </div>
        {unreadNotifications.length > 0 && (
          <Button variant="outline" onClick={markAllAsRead} data-testid="mark-all-read-btn">
            <Check className="w-4 h-4 mr-2" />
            Mark All as Read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No notifications</h3>
            <p className="text-muted-foreground">
              You're all caught up! Check back later for updates.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notif) => (
            <Card 
              key={notif.notification_id} 
              className={`transition-all ${!notif.is_read ? 'border-primary shadow-sm' : ''} ${getNotificationStyle(notif)}`}
              data-testid={`notification-${notif.notification_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${!notif.is_read ? 'bg-primary/10' : 'bg-muted'}`}>
                    {getNotificationIcon(notif)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className={`font-medium ${!notif.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notif.title}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
                      </div>
                      {!notif.is_read && (
                        <Badge variant="default" className="flex-shrink-0">New</Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                      {!notif.is_read && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => markAsRead(notif.notification_id)}
                          data-testid={`mark-read-${notif.notification_id}`}
                        >
                          Mark as read
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Quick Link to Payments */}
      {notifications.some(n => n.title?.toLowerCase().includes('payment') || n.message?.toLowerCase().includes('payment')) && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-primary" />
                <div>
                  <h3 className="font-semibold">Need to make a payment?</h3>
                  <p className="text-sm text-muted-foreground">
                    View your invoices and upload proof of payment
                  </p>
                </div>
              </div>
              <Link to="/dashboard/payments">
                <Button data-testid="go-to-payments-btn">Go to Payments</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
    </MemberLayout>
  );
};

export default MemberNotifications;
